/**
 * What a customer is told before they connect a number, and the record that they were told.
 *
 * This is the only module in the repo whose value is entirely in its wording, so the copy
 * lives here as data rather than as JSX. Three reasons, all practical. The pairing route
 * refuses to start a session without a stored acceptance of the *current* version, so the
 * version and the text it refers to have to be one thing that moves together. The same words
 * have to appear in onboarding and at the pairing screen, and two hand-maintained copies of a
 * legal statement drift the first time one is edited. And a test can assert that the four
 * claims the product is legally and morally obliged to make are still present, which it cannot
 * do against markup.
 *
 * On the tone. The temptation is to soften this into a checkbox nobody reads, and the opposite
 * temptation is to frighten the customer out of a product that works. Both are wrong for the
 * same reason: the customer is going to find out either way, and the only question is whether
 * they find out from us before they connect their main line or from WhatsApp afterwards. So
 * every point below is specific enough to act on — which number to use, what a ban actually
 * takes with it, what we do about it and what that does not buy — and none of it is hedged.
 */

/**
 * Bumping this re-prompts every user who accepted an older one.
 *
 * Tied to the *substance* of what is claimed, not to typography: fixing a comma should not
 * make a customer re-accept, and quietly weakening a warning should be impossible without it.
 */
export const DISCLOSURE_VERSION = 1

export interface DisclosurePoint {
  /** Readable at a glance. The heading alone must not be reassuring on its own. */
  heading: string
  body: string
}

export const DISCLOSURE_TITLE = 'How Wami connects to WhatsApp'

export const DISCLOSURE_LEAD =
  'Wami links your number the same way WhatsApp Web does — you scan a QR code from the phone ' +
  'that owns the number. That single choice is why your existing chats arrive with you, why ' +
  'groups work, and why there is no fee per message and no template to get approved. It is ' +
  'also not an arrangement Meta has agreed to. This takes a minute to read and it is the part ' +
  'people wish they had read.'

export const DISCLOSURE_POINTS: DisclosurePoint[] = [
  {
    heading: 'This is not the official WhatsApp Business API',
    body:
      'Meta sells an official API, directly and through approved providers. Wami is not one of ' +
      'them. We speak the same protocol the WhatsApp desktop and web apps speak, through an ' +
      'open-source implementation of it. Meta has not reviewed us, approved us, or partnered ' +
      'with us, and nothing here carries their endorsement.',
  },
  {
    heading: "It goes against WhatsApp's terms of service",
    body:
      'WhatsApp’s terms prohibit accessing the service through unauthorised or automated ' +
      'means. Connecting a number to Wami is exactly that. You are the account holder, so this ' +
      'is your decision to make rather than ours — but you should make it knowing that is ' +
      'what you are doing.',
  },
  {
    heading: 'A connected number can be banned, without warning',
    body:
      'WhatsApp can suspend or permanently ban a number it decides is being used this way. ' +
      'There is usually no warning, no explanation, and no appeal that reliably works. A ban ' +
      'takes the number’s chats, groups and history with it on the phone as well as here, ' +
      'and nobody at Wami can get any of it back for you.',
  },
  {
    heading: 'Use a number you could afford to lose',
    body:
      'Do not connect the number printed on your invoices, on your storefront and in ten years ' +
      'of customer phones. Do not connect a team member’s personal number. Buy a number for ' +
      'this, and connect a second one as a spare — if the first stops mid-campaign, Wami ' +
      'moves the rest of the send to the spare instead of stalling, and it can only do that if ' +
      'a spare is connected.',
  },
  {
    heading: 'What we do about the risk, and what it does not buy you',
    body:
      'New numbers ramp up over three weeks instead of starting at full volume, sends go out at ' +
      'varied human intervals rather than in bursts, audiences are checked before they are ' +
      'messaged so nothing is sent to numbers that are not on WhatsApp, and a number whose ' +
      'messages start being refused is stood down on its own. Each of these measurably lowers ' +
      'the risk. None of them makes it low enough to run a business you could not rebuild on a ' +
      'single number.',
  },
  {
    heading: 'There is no support line behind this',
    body:
      'WhatsApp can change the protocol without notice, and has done. When that happens, sending ' +
      'stops until we ship a fix, and there is nobody at Meta to escalate to — from their ' +
      'side this connection was never supposed to exist. Wami will tell you when it happens ' +
      'rather than leaving a queue looking healthy.',
  },
]

/**
 * The sentence beside the checkbox.
 *
 * Deliberately two commitments rather than one "I agree". The first is the part we cannot
 * verify and genuinely need — a person connecting a number they do not own is the one case
 * where this product could damage somebody who never chose to use it. The second is the part
 * that is actually about them.
 */
export const DISCLOSURE_ACKNOWLEDGEMENT =
  'I own the numbers I connect, or I am authorised to act for whoever does. I understand they ' +
  'can be banned, and I have chosen numbers whose loss I could live with.'

export const DISCLOSURE_ACCEPT_LABEL = 'I understand — let me connect a number'

/** Shown where there is room for one line and not for six — a list row, a settings summary. */
export const DISCLOSURE_SUMMARY =
  'Wami drives WhatsApp through an unofficial client. It breaches WhatsApp’s terms and ' +
  'connected numbers can be banned.'

export interface DisclosureRecord {
  version: number
  acceptedAt: Date
}

/**
 * Whether this person has accepted the version currently in force.
 *
 * A newer stored version also counts, which only matters if a deploy is rolled back: somebody
 * who read a *later* draft has not been under-informed, and re-prompting them would be noise.
 */
export function disclosureAccepted(
  record: DisclosureRecord | null | undefined,
  version: number = DISCLOSURE_VERSION,
): boolean {
  if (!record) return false
  return record.version >= version
}

/** Why pairing was refused, in the customer's words rather than as a status code. */
export const DISCLOSURE_REQUIRED_MESSAGE =
  'Before connecting a number you need to read and accept how Wami connects to WhatsApp.'
